import React, { useState, useEffect, useRef } from 'react';
import { 
  Layout, 
  Typography, 
  Button, 
  Spin, 
  message, 
  Tooltip, 
  Avatar, 
  Popover,
  Checkbox,
  Radio,
  Input
} from 'antd';
import { 
  LogoutOutlined, 
  UserOutlined,
  RightOutlined,
  LeftOutlined,
  StarOutlined
} from '@ant-design/icons';
import { toPng } from 'html-to-image';
import AgentConfigService from '../services/AgentConfigService';
import SessionService from '../services/SessionService';

const { Sider } = Layout;
const { Text } = Typography;
const { TextArea } = Input;

/**
 * AgentSider - Reusable sidebar component for agent pages
 * 
 * Features:
 * - Saved sessions list with click to restore
 * - Feedback button with screenshot capture
 * - User profile and logout
 * - Collapsible design
 * 
 * Props:
 * @param {string} username - Current user's name
 * @param {string} sessionId - Current session ID
 * @param {string} agentId - Current agent ID
 * @param {function} onLogout - Logout callback
 * @param {boolean} collapsed - External control for collapsed state (optional)
 * @param {function} onCollapsedChange - Callback when collapsed state changes (optional)
 * @param {Array} savedSessions - Array of saved session objects
 * @param {boolean} sessionsLoading - Loading state for sessions
 * @param {function} onSessionClick - Callback when a saved session is clicked
 * @param {function} onLoadSessions - Callback to refresh sessions list
 * @param {Object} apiData - Current API data for feedback screenshot
 * @param {Object} currentWorkflowState - Current workflow state
 * @param {React.RefObject} screenshotRef - Ref to the element to capture for screenshots
 */
const AgentSider = ({
  username,
  sessionId,
  agentId,
  onLogout,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  savedSessions = [],
  sessionsLoading = false,
  onSessionClick,
  onLoadSessions,
  apiData,
  currentWorkflowState,
  screenshotRef
}) => {
  // Internal collapsed state (used if not externally controlled)
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  
  // Determine if collapsed is controlled externally or internally
  const isControlled = controlledCollapsed !== undefined;
  const siderCollapsed = isControlled ? controlledCollapsed : internalCollapsed;

  // Feedback states
  const [feedbackPopoverVisible, setFeedbackPopoverVisible] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState(null);
  const [feedbackOptionsSelected, setFeedbackOptionsSelected] = useState([]);
  const [feedbackComments, setFeedbackComments] = useState('');
  const [feedbackScreenshot, setFeedbackScreenshot] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Toggle collapsed state
  const toggleSider = () => {
    const newCollapsed = !siderCollapsed;
    if (isControlled && onCollapsedChange) {
      onCollapsedChange(newCollapsed);
    } else {
      setInternalCollapsed(newCollapsed);
    }
  };

  // Load feedback config when agent changes
  useEffect(() => {
    const loadFeedbackConfig = async () => {
      try {
        const config = await AgentConfigService.getFeedbackConfigForAgent(agentId, sessionId);
        setFeedbackConfig(config);
      } catch (error) {
        console.error('Error loading feedback config:', error);
        setFeedbackConfig({ options: [], input_type: 'checkbox' });
      }
    };
    
    if (agentId && sessionId) {
      loadFeedbackConfig();
    }
  }, [agentId, sessionId]);

  // Handle feedback button click - capture screenshot first, then open popover
  const handleFeedbackClick = async () => {
    try {
      // Capture screenshot BEFORE opening popover
      if (screenshotRef?.current) {
        const dataUrl = await toPng(screenshotRef.current, {
          quality: 0.8,
          backgroundColor: '#ffffff',
          filter: (node) => {
            // Exclude the feedback popover if it somehow exists
            return !node.classList?.contains('feedback-popover');
          }
        });
        setFeedbackScreenshot(dataUrl);
      }
      
      // Reset form state
      setFeedbackOptionsSelected([]);
      setFeedbackComments('');
      
      // Open popover
      setFeedbackPopoverVisible(true);
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      // Still open popover even if screenshot fails
      setFeedbackPopoverVisible(true);
    }
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async () => {
    try {
      setFeedbackSubmitting(true);
      
      const feedbackData = {
        agent_id: agentId,
        agent_key: currentWorkflowState?.agent_key || null,
        options_selected: feedbackOptionsSelected,
        comments: feedbackComments,
        screenshot_base64: feedbackScreenshot || '',
        redis_data: apiData || {},
        timestamp: new Date().toISOString()
      };
      
      const result = await AgentConfigService.submitFeedback(sessionId, feedbackData);
      
      if (result.success) {
        message.success('Feedback submitted successfully. Thank you!');
        setFeedbackPopoverVisible(false);
        setFeedbackOptionsSelected([]);
        setFeedbackComments('');
        setFeedbackScreenshot(null);
      } else {
        message.error('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      message.error('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Handle checkbox/radio change for feedback
  const handleFeedbackOptionChange = (values) => {
    if (feedbackConfig?.input_type === 'radio') {
      setFeedbackOptionsSelected([values.target.value]);
    } else {
      setFeedbackOptionsSelected(values);
    }
  };

  // Render feedback popover content
  const renderFeedbackContent = () => {
    const hasOptions = feedbackConfig?.options && feedbackConfig.options.length > 0;
    const isRadio = feedbackConfig?.input_type === 'radio';
    
    return (
      <div style={{ width: '350px' }} className="feedback-popover">
        <div style={{ marginBottom: '16px', color: '#333' }}>
          The data and the screenshot will automatically be saved with the feedback. 
          If you have specific comments, please provide it here. Thank you for your feedback!
        </div>
        
        {hasOptions && (
          <div style={{ marginBottom: '16px' }}>
            {isRadio ? (
              <Radio.Group 
                onChange={handleFeedbackOptionChange}
                value={feedbackOptionsSelected[0]}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {feedbackConfig.options.map(option => (
                    <Radio key={option.id} value={option.id}>
                      {option.label}
                    </Radio>
                  ))}
                </div>
              </Radio.Group>
            ) : (
              <Checkbox.Group 
                onChange={handleFeedbackOptionChange}
                value={feedbackOptionsSelected}
              >
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '8px' 
                }}>
                  {feedbackConfig.options.map(option => (
                    <Checkbox key={option.id} value={option.id}>
                      {option.label}
                    </Checkbox>
                  ))}
                </div>
              </Checkbox.Group>
            )}
          </div>
        )}
        
        <TextArea
          rows={4}
          placeholder="Additional comments (optional)"
          value={feedbackComments}
          onChange={(e) => setFeedbackComments(e.target.value)}
          style={{ marginBottom: '16px' }}
        />
        
        <div style={{ textAlign: 'center' }}>
          <Button 
            type="primary" 
            onClick={handleFeedbackSubmit}
            loading={feedbackSubmitting}
            style={{ 
              backgroundColor: '#1890ff',
              minWidth: '100px'
            }}
          >
            OK
          </Button>
        </div>
      </div>
    );
  };

  // Handle logout
  const handleLogout = async () => {
    await SessionService.logout(sessionId, onLogout);
  };

  return (
    <Sider 
      width={240} 
      collapsible 
      collapsed={siderCollapsed}
      collapsedWidth={100}
      trigger={null}
      className="sidebar-gradient"
      style={{ 
        display: 'flex', 
        flexDirection: 'column',
        zIndex: 1001,
        height: '100vh',
        position: 'fixed',
        left: 0
      }}
    >
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        paddingTop: '20px'
      }}>
        {/* Header with Saved Sessions title and collapse toggle */}
        <div style={{ 
          padding: '16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: siderCollapsed ? 'center' : 'space-between'
        }}>
          {!siderCollapsed && (
            <Text style={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
              Saved Sessions
            </Text>
          )}
          <Button 
            type="text"
            icon={siderCollapsed ? <RightOutlined style={{ color: 'white' }} /> : <LeftOutlined style={{ color: 'white' }} />}
            onClick={toggleSider}
            style={{ color: 'white', padding: '4px' }}
            size="small"
          />
        </div>
        
        {/* Saved Sessions List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: siderCollapsed ? '8px' : '16px' }}>
          {sessionsLoading ? (
            <div style={{ textAlign: 'center', color: 'white', padding: '16px' }}>
              <Spin size="small" />
              {!siderCollapsed && <div style={{ marginTop: '8px' }}>Loading...</div>}
            </div>
          ) : savedSessions.length > 0 ? (
            savedSessions.map((session) => (
              <div 
                key={session.id}
                style={{ 
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onClick={() => onSessionClick && onSessionClick(session.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <div style={{ fontWeight: 'bold', color: 'white', marginBottom: '4px' }}>
                  {!siderCollapsed && session.name}
                  {siderCollapsed && session.name.charAt(0)}
                </div>
                {!siderCollapsed && (
                  <>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {session.date}
                    </div>
                    {session.component_count && (
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
                        {session.component_count} components
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          ) : (
            <div style={{ 
              textAlign: 'center', 
              color: 'rgba(255, 255, 255, 0.7)',
              padding: '16px',
              fontSize: siderCollapsed ? '12px' : '14px'
            }}>
              {!siderCollapsed ? 'No saved sessions' : '...'}
            </div>
          )}
        </div>
        
        {/* Feedback Button */}
        <div style={{ 
          padding: '12px 16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <Popover
            content={renderFeedbackContent()}
            title={null}
            trigger="click"
            open={feedbackPopoverVisible}
            onOpenChange={(visible) => {
              if (!visible) {
                setFeedbackPopoverVisible(false);
              }
            }}
            placement="rightTop"
            overlayStyle={{ 
              maxWidth: '400px'
            }}
            overlayInnerStyle={{
              borderRadius: '12px',
              padding: '16px'
            }}
          >
            <Button
              type="text"
              onClick={handleFeedbackClick}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: siderCollapsed ? 'center' : 'flex-start',
                color: 'white',
                padding: siderCollapsed ? '8px' : '8px 12px',
                height: 'auto',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <StarOutlined style={{ fontSize: '18px', color: 'white' }} />
              {!siderCollapsed && (
                <span style={{ marginLeft: '8px', color: 'white' }}>Feedback</span>
              )}
            </Button>
          </Popover>
        </div>
        
        {/* User Profile Section */}
        <div style={{ 
          padding: '16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: siderCollapsed ? 0 : '8px' }} />
            {!siderCollapsed && (
              <Text style={{ color: 'white' }} ellipsis>
                {username}
              </Text>
            )}
          </div>
          <Tooltip title="Logout">
            <Button 
              type="text"
              icon={<LogoutOutlined style={{ color: 'white' }} />}
              onClick={handleLogout}
              style={{ color: 'white', padding: '4px' }}
              size="small"
            />
          </Tooltip>
        </div>
      </div>
    </Sider>
  );
};

// Export collapsed width constants for layout calculations
export const SIDER_WIDTH = 240;
export const SIDER_COLLAPSED_WIDTH = 100;

export default AgentSider;